/**
 * `/follow-ups` — the follow-up queue (S9).
 *
 * `Lead.followUpDate` was captured, stored and indexed from the first day and
 * displayed nowhere, which made every date typed at a booth a note to nobody.
 * This is where they get worked.
 *
 * Reads the board's own leads, so the list itself works offline; the two row
 * actions POST and therefore do not. That split is deliberate — seeing what you
 * owe is more useful in a dead zone than rescheduling it, and the alternative
 * (a new online-only endpoint) would have made the whole screen blank without
 * signal for no gain.
 *
 * Not scoped to the app bar's event Select — see `lib/follow-up-queue.ts`.
 */
import { followUpDateIn } from '@paradise/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { FollowUpBadge } from '@/components/follow-up-badge';
import { useSetFollowUpMutation } from '@/components/lead-detail/use-lead-detail';
import { Button } from '@/components/ui/button';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import { Spinner } from '@/components/ui/spinner';
import { useBoardLeads, type BoardLead } from '@/lib/board-data';
import { groupFollowUps } from '@/lib/follow-up-queue';
import { useIsOffline } from '@/lib/offline';
import { STAGE_UI } from '@/lib/stages';

export const Route = createFileRoute('/_app/follow-ups')({
  staticData: { barSubtitle: 'Follow-ups', showEventSelect: false },
  component: FollowUpsScreen
});

interface QueueRowProps {
  lead: BoardLead;
  disabled: boolean;
  pending: boolean;
  onSet: (followUpDate: string | null) => void;
}

function QueueRow({ lead, disabled, pending, onSet }: QueueRowProps) {
  const ui = STAGE_UI[lead.stage];

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-border px-3 py-2.5 first:border-t-0">
      <Link to="/leads/$leadId" params={{ leadId: lead.id }} className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="font-heading truncate text-[15px] font-semibold">{lead.company}</span>
          <span className="shrink-0 text-[11px] text-muted-foreground">
            {lead.ref !== null ? `#${lead.ref}` : 'Pending'}
          </span>
        </div>
        <div className="mt-0.5 flex items-center gap-1.5 truncate text-[12px] text-muted-foreground">
          <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: ui.dot }} aria-hidden="true" />
          {ui.label}
          {lead.contactName !== null && <> · {lead.contactName}</>}
        </div>
      </Link>

      <FollowUpBadge followUpDate={lead.followUpDate} />

      <div className="flex shrink-0 items-center gap-1">
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={disabled || pending}
          onClick={() => onSet(followUpDateIn(7))}
        >
          Next week
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="text-muted-foreground"
          disabled={disabled || pending}
          onClick={() => onSet(null)}
        >
          Clear
        </Button>
      </div>
    </div>
  );
}

function FollowUpsScreen() {
  const { leads, isLoading } = useBoardLeads();
  const isOffline = useIsOffline();
  const setFollowUp = useSetFollowUpMutation();
  const [failedFor, setFailedFor] = useState<string | null>(null);

  // Grouped once per render against a single `new Date()` — computing "today"
  // per row could straddle midnight and put two leads with the same date in
  // two different groups.
  const groups = useMemo(() => groupFollowUps(leads), [leads]);

  function push(leadId: string, followUpDate: string | null): void {
    setFailedFor(null);
    setFollowUp.mutate(
      { leadId, followUpDate },
      { onSuccess: result => setFailedFor(result.kind === 'ok' ? null : leadId) }
    );
  }

  if (isLoading) {
    return (
      <p className="text-muted-foreground mx-auto flex w-full max-w-220 items-center gap-2 p-4 text-sm">
        <Spinner className="size-4" aria-hidden="true" /> Loading…
      </p>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-220 flex-col gap-4 p-3 sm:p-4">
      {groups.length === 0 ? (
        <Empty>
          <EmptyHeader>
            <EmptyTitle>Nothing to follow up</EmptyTitle>
            <EmptyDescription>
              Follow-up dates set on a buyer — at the booth or from their screen — collect here.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        groups.map(group => (
          <section key={group.key}>
            <div className="mb-1.5 flex items-baseline gap-2">
              <h2 className="text-xs font-semibold tracking-wider text-primary uppercase">{group.title}</h2>
              <span className="text-xs text-muted-foreground">{group.leads.length}</span>
              {group.hint !== null && <span className="text-[11px] text-muted-foreground">{group.hint}</span>}
            </div>
            <div className="rounded-xl bg-card ring-1 ring-foreground/10">
              {group.leads.map(lead => (
                <QueueRow
                  key={lead.id}
                  lead={lead}
                  // A draft has no server id to address, exactly as on the lead screen.
                  disabled={isOffline || lead.isPending}
                  pending={setFollowUp.isPending && setFollowUp.variables?.leadId === lead.id}
                  onSet={followUpDate => push(lead.id, followUpDate)}
                />
              ))}
            </div>
          </section>
        ))
      )}

      {isOffline && groups.length > 0 && (
        <p className="text-xs text-muted-foreground">Rescheduling needs a connection. The list itself is up to date.</p>
      )}
      {failedFor !== null && <p className="text-xs text-destructive">That didn't save. Try again in a moment.</p>}
    </div>
  );
}
